/**
 * One scan, watched on two channels at once.
 *
 * **Firestore `onSnapshot` is the primary.** A scan runs 65–95 seconds. Mobile
 * Safari suspends background tabs, phones lock, lifts have no signal — and a
 * document listener resyncs on its own when the tab comes back. Everything the
 * screen strictly needs is written there.
 *
 * **SSE is the enhancement**: per-branch pills and the verdict token by token,
 * which are far too chatty for Firestore. It is allowed to drop dead; when it
 * does, the screen keeps filling in from the snapshots, one phase at a time.
 *
 * The reducer takes both and never regresses: a snapshot only fills in what the
 * stream has not already said, because the stream is always the fresher of the
 * two while it is alive.
 */
import { doc, onSnapshot } from 'firebase/firestore'
import { useEffect, useReducer, useRef } from 'react'

import { boot } from '../lib/boot'
import { streamScan } from '../lib/api'
import { STAGES } from '../lib/types'
import type {
  BottleCard,
  PairingGuide,
  PanelAnswer,
  ProgressEvent,
  RankedEntry,
  ScanDocument,
  ScanResult,
  Stage,
  WineMatch,
} from '../lib/types'

export type StageState = 'waiting' | 'running' | 'done'

export interface ScanState {
  status: 'connecting' | 'running' | 'complete' | 'failed'
  phase: string
  detected: number | null
  sceneNotes: string
  cards: BottleCard[]
  stages: Record<string, Partial<Record<Stage, StageState>>>
  matched: Record<string, WineMatch>
  collapsed: Record<string, string>
  ranking: RankedEntry[]
  pairing: PairingGuide | null
  verdict: string
  result: ScanResult | null
  /** The four critics on a shopping shelf. Arrives after the verdict, and stays
   *  empty for a cellar scan. */
  shelfAnswers: PanelAnswer[]
  error: string | null
  narration: string[]
  live: boolean
}

const EMPTY: ScanState = {
  status: 'connecting',
  phase: 'Waking the sommelier',
  detected: null,
  sceneNotes: '',
  cards: [],
  stages: {},
  matched: {},
  collapsed: {},
  ranking: [],
  pairing: null,
  verdict: '',
  result: null,
  shelfAnswers: [],
  error: null,
  narration: [],
  live: false,
}

type Action =
  | { kind: 'event'; event: ProgressEvent }
  | { kind: 'snapshot'; document: ScanDocument }
  | { kind: 'live'; live: boolean }
  | { kind: 'reset' }

function withStage(
  state: ScanState,
  slotId: string,
  stage: Stage,
  value: StageState,
): ScanState {
  const before = state.stages[slotId] ?? {}
  // Never walk a pill backwards: a late `branch_started` after the branch has
  // already reported its output would otherwise un-tick it.
  if (before[stage] === 'done' && value !== 'done') return state
  return { ...state, stages: { ...state.stages, [slotId]: { ...before, [stage]: value } } }
}

function reduce(state: ScanState, action: Action): ScanState {
  if (action.kind === 'reset') return EMPTY
  if (action.kind === 'live') return { ...state, live: action.live }

  if (action.kind === 'snapshot') {
    const document = action.document
    const status =
      document.status === 'complete'
        ? 'complete'
        : document.status === 'failed'
          ? 'failed'
          : 'running'
    return {
      ...state,
      status: state.status === 'complete' ? 'complete' : status,
      phase: state.live ? state.phase : (document.phase ?? state.phase),
      detected: state.detected ?? document.bottlesDetected ?? null,
      sceneNotes: state.sceneNotes || (document.sceneNotes ?? ''),
      cards: state.cards.length ? state.cards : (document.cards ?? []),
      matched: Object.keys(state.matched).length ? state.matched : (document.matched ?? {}),
      collapsed: Object.keys(state.collapsed).length
        ? state.collapsed
        : (document.collapsed ?? {}),
      ranking: state.ranking.length ? state.ranking : (document.ranking ?? []),
      pairing: state.pairing ?? document.pairingGuide ?? null,
      result: state.result ?? document.result ?? null,
      shelfAnswers: state.shelfAnswers.length
        ? state.shelfAnswers
        : (document.shelfAnswers ?? []),
      error: state.error ?? document.error ?? null,
    }
  }

  const event = action.event
  switch (event.type) {
    case 'stream_open':
      return { ...state, status: state.status === 'connecting' ? 'running' : state.status }
    case 'bottles_detected':
      return {
        ...state,
        status: 'running',
        detected: event.count,
        sceneNotes: event.sceneNotes ?? '',
        phase: 'Looking at the bottles',
      }
    case 'bottles_ready': {
      const stages = { ...state.stages }
      for (const card of event.bottles) stages[card.slotId] ??= {}
      return { ...state, cards: event.bottles, stages, phase: 'Researching each bottle' }
    }
    case 'branch_started':
      return withStage(state, event.slotId, event.stage, 'running')
    case 'branch_done':
      return withStage(state, event.slotId, event.stage, 'done')
    case 'wine_matched':
      return {
        ...state,
        phase: 'Checking your cellar',
        matched: { ...state.matched, [event.slotId]: event },
      }
    case 'bottle_collapsed':
      return {
        ...state,
        collapsed: { ...state.collapsed, [event.slotId]: event.intoSlotId },
      }
    case 'ranking_ready':
      return { ...state, ranking: event.ranking, phase: 'Ranking them' }
    case 'pairing_ready':
      return { ...state, pairing: event.pairingGuide, phase: 'Planning a meal' }
    case 'verdict_delta':
      return {
        ...state,
        phase: 'Writing the verdict',
        verdict: state.verdict + event.text,
      }
    case 'progress':
      return { ...state, narration: [...state.narration.slice(-4), event.text] }
    case 'progress_snapshot':
      return { ...state, phase: event.phase ?? state.phase, cards: event.cards ?? state.cards }
    case 'shelf_answers':
      // The run said "Done" a few seconds ago and meant it; the phase only left
      // "Done" while the critics were being asked, so put it back.
      return { ...state, phase: 'Done', shelfAnswers: event.answers }
    case 'run_done':
      return {
        ...state,
        status: 'complete',
        phase: 'Done',
        result: event.result,
        ranking: event.result?.ranking?.length ? event.result.ranking : state.ranking,
        verdict: state.verdict || (event.result?.verdict ?? ''),
      }
    case 'run_failed':
      return { ...state, status: 'failed', error: event.error }
    default:
      return state
  }
}

export function useScan(runId: string | undefined): ScanState {
  const [state, dispatch] = useReducer(reduce, EMPTY)
  const finished = useRef(false)

  useEffect(() => {
    if (!runId) return
    dispatch({ kind: 'reset' })
    finished.current = false
    const controller = new AbortController()
    let unsubscribe: (() => void) | undefined

    void (async () => {
      const { db } = await boot()
      if (controller.signal.aborted) return
      unsubscribe = onSnapshot(
        doc(db, 'scan_runs', runId),
        (snapshot) => {
          const data = snapshot.data() as ScanDocument | undefined
          if (data) dispatch({ kind: 'snapshot', document: data })
        },
        // A rules rejection or a dropped listener is survivable: the SSE
        // channel is still running, and the screen has a Retry.
        (error) => console.warn('scan listener', error),
      )

      // The stream is reconnected while the run is unfinished, because a phone
      // waking up gets a fresh one and the server replays its history.
      while (!controller.signal.aborted && !finished.current) {
        try {
          dispatch({ kind: 'live', live: true })
          for await (const event of streamScan(runId, controller.signal)) {
            dispatch({ kind: 'event', event })
            if (event.type === 'run_done' || event.type === 'run_failed') {
              finished.current = true
            }
          }
        } catch (error) {
          if (controller.signal.aborted) return
          console.warn('scan stream', error)
        }
        dispatch({ kind: 'live', live: false })
        if (finished.current || controller.signal.aborted) break
        await new Promise((resolve) => setTimeout(resolve, 1500))
      }
    })()

    return () => {
      controller.abort()
      unsubscribe?.()
    }
  }, [runId])

  return state
}

export { STAGES }
